import type { ItemType } from "@mimber/ui-web"
import type { PublicFeatureFlagDto } from "@mimber/schema"
import { Resource, Action } from "@mimber/schema"
import {
  DashboardOutlined,
  TeamOutlined,
  CalendarOutlined,
  BellOutlined,
  DollarOutlined,
  AuditOutlined,
  FileOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  IdcardOutlined,
  MailOutlined,
  FormOutlined,
  MessageOutlined,
  SendOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  FileProtectOutlined,
  PictureOutlined,
  BookOutlined,
  ProfileOutlined,
  LockOutlined,
  ApiOutlined,
  CreditCardOutlined,
  ControlOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  SwapOutlined,
  HeartOutlined,
  TrophyOutlined,
  HomeOutlined,
} from "@mimber/ui-web"

// Route to menu key mapping
export const ROUTE_TO_KEY_MAP: Record<string, string> = {
  "/dashboard": "dashboard-overview",
  "/dashboard/overview": "dashboard-overview",
  "/dashboard/recent-activity": "dashboard-recent-activity",
  "/dashboard/calendar": "dashboard-calendar",
  "/members": "members-directory",
  "/members/directory": "members-directory",
  "/members/groups-committees": "members-groups-committees",
  "/members/membership-tiers": "members-membership-tiers",
  "/members/invites": "members-invites",
  "/members/households": "members-households",
  "/events": "events-all-events",
  "/events/all-events": "events-all-events",
  "/events/volunteering": "events-volunteering",
  "/events/calendar": "events-calendar",
  "/events/attendance": "events-attendance",
  "/communication": "communication-announcements",
  "/communication/announcements": "communication-announcements",
  "/communication/newsletters": "communication-newsletters",
  "/communication/surveys": "communication-surveys",
  "/communication/messages": "communication-messages",
  "/communication/email-campaigns": "communication-email-campaigns",
  "/communication/templates": "communication-templates",
  "/finance": "finance-overview",
  "/finance/overview": "finance-overview",
  "/finance/invoices": "finance-invoices",
  "/finance/orders": "finance-orders",
  "/finance/dues": "finance-dues",
  "/finance/donations": "finance-donations",
  "/finance/fundraising": "finance-fundraising",
  "/finance/transactions": "finance-transactions",
  "/finance/reports": "finance-reports",
  "/governance": "governance-elections",
  "/governance/elections": "governance-elections",
  "/governance/voting": "governance-voting",
  "/governance/bylaws": "governance-bylaws",
  "/content": "content-documents",
  "/content/documents": "content-documents",
  "/content/media": "content-media",
  "/content/resources": "content-resources",
  "/organization-settings": "settings-general",
  "/organization-settings/general": "settings-general",
  "/organization-settings/organization-profile":
    "settings-organization-profile",
  "/organization-settings/roles-permissions": "settings-roles-permissions",
  "/organization-settings/integrations": "settings-integrations",
  "/organization-settings/billing": "settings-billing",
}

export const getMenuKeyFromPathname = (
  pathname: string
): string | undefined => {
  const exactMatch = ROUTE_TO_KEY_MAP[pathname]

  if (exactMatch) {
    return exactMatch
  }

  const longestPrefix = Object.keys(ROUTE_TO_KEY_MAP)
    .filter((routePath) => pathname.startsWith(`${routePath}/`))
    .sort((firstRoutePath, secondRoutePath) => {
      return secondRoutePath.length - firstRoutePath.length
    })[0]

  return longestPrefix ? ROUTE_TO_KEY_MAP[longestPrefix] : undefined
}

/**
 * Navigation menu items configuration
 * NOTE: Menu item keys correlate to feature toggle keys by convention
 * If a feature toggle exists with the same key and enabled=false, the menu item will be hidden
 */
export const NAVIGATION_ITEMS: ItemType[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <DashboardOutlined />,
    children: [
      {
        key: "dashboard-overview",
        icon: <DashboardOutlined />,
        label: "Overview",
      },
      {
        key: "dashboard-recent-activity",
        icon: <ClockCircleOutlined />,
        label: "Recent Activity",
      },
      {
        key: "dashboard-calendar",
        icon: <CalendarOutlined />,
        label: "Calendar",
      },
    ],
  },
  {
    key: "members",
    label: "Members",
    icon: <TeamOutlined />,
    children: [
      {
        key: "members-directory",
        icon: <TeamOutlined />,
        label: "Directory",
      },
      {
        key: "members-groups-committees",
        icon: <UsergroupAddOutlined />,
        label: "Groups & Committees",
      },
      {
        key: "members-membership-tiers",
        icon: <IdcardOutlined />,
        label: "Membership Tiers",
      },
      {
        key: "members-invites",
        icon: <MailOutlined />,
        label: "Invites",
      },
      {
        key: "members-households",
        icon: <HomeOutlined />,
        label: "Households",
      },
    ],
  },
  {
    key: "events",
    label: "Events",
    icon: <CalendarOutlined />,
    children: [
      {
        key: "events-all-events",
        icon: <CalendarOutlined />,
        label: "All Events",
      },
      {
        key: "events-volunteering",
        icon: <HeartOutlined />,
        label: "Volunteering",
      },
      {
        key: "events-calendar",
        icon: <CalendarOutlined />,
        label: "Calendar",
      },
      {
        key: "events-attendance",
        icon: <CheckSquareOutlined />,
        label: "Attendance",
      },
    ],
  },
  {
    key: "communication",
    label: "Communication",
    icon: <BellOutlined />,
    children: [
      {
        key: "communication-announcements",
        icon: <BellOutlined />,
        label: "Announcements",
      },
      {
        key: "communication-newsletters",
        icon: <FileTextOutlined />,
        label: "Newsletters",
      },
      {
        key: "communication-surveys",
        icon: <FormOutlined />,
        label: "Surveys",
      },
      {
        key: "communication-messages",
        icon: <MessageOutlined />,
        label: "Messages",
      },
      {
        key: "communication-email-campaigns",
        icon: <SendOutlined />,
        label: "Email Campaigns",
      },
      {
        key: "communication-templates",
        icon: <MailOutlined />,
        label: "Templates",
      },
    ],
  },
  {
    key: "finance",
    label: "Finance",
    icon: <DollarOutlined />,
    children: [
      {
        key: "finance-overview",
        icon: <DashboardOutlined />,
        label: "Overview",
      },
      {
        key: "finance-invoices",
        icon: <FileTextOutlined />,
        label: "Invoices",
      },
      {
        key: "finance-orders",
        icon: <ShoppingOutlined />,
        label: "Orders",
      },
      {
        key: "finance-dues",
        icon: <DollarOutlined />,
        label: "Dues",
      },
      {
        key: "finance-donations",
        icon: <GiftOutlined />,
        label: "Donations",
      },
      {
        key: "finance-fundraising",
        icon: <TrophyOutlined />,
        label: "Fundraising",
      },
      {
        key: "finance-transactions",
        icon: <SwapOutlined />,
        label: "Transactions",
      },
      {
        key: "finance-reports",
        icon: <BarChartOutlined />,
        label: "Reports",
      },
    ],
  },
  {
    key: "governance",
    label: "Governance",
    icon: <AuditOutlined />,
    children: [
      {
        key: "governance-elections",
        icon: <AuditOutlined />,
        label: "Elections",
      },
      {
        key: "governance-voting",
        icon: <CheckSquareOutlined />,
        label: "Voting",
      },
      {
        key: "governance-bylaws",
        icon: <FileProtectOutlined />,
        label: "Bylaws",
      },
    ],
  },
  {
    key: "content",
    label: "Content",
    icon: <FileOutlined />,
    children: [
      {
        key: "content-documents",
        icon: <FileOutlined />,
        label: "Documents",
      },
      {
        key: "content-media",
        icon: <PictureOutlined />,
        label: "Media",
      },
      {
        key: "content-resources",
        icon: <BookOutlined />,
        label: "Resources",
      },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    icon: <SettingOutlined />,
    children: [
      {
        key: "settings-general",
        icon: <ControlOutlined />,
        label: "General Settings",
      },
      {
        key: "settings-organization-profile",
        icon: <ProfileOutlined />,
        label: "Organization Profile",
      },
      {
        key: "settings-roles-permissions",
        icon: <LockOutlined />,
        label: "Roles & Permissions",
      },
      {
        key: "settings-integrations",
        icon: <ApiOutlined />,
        label: "Integrations",
      },
      {
        key: "settings-billing",
        icon: <CreditCardOutlined />,
        label: "Billing",
      },
    ],
  },
]

/**
 * Filter navigation items based on feature toggles
 * Removes children that are disabled by feature flags
 * Removes parent items that have no enabled children remaining
 *
 * @param items - Navigation items to filter
 * @param featureToggles - Array of feature flags
 * @returns Filtered navigation items
 */
export const filterNavigationByFeatureToggles = (
  items: ItemType[],
  featureToggles: PublicFeatureFlagDto[]
): ItemType[] => {
  // Build a Set of enabled feature keys
  // A key is enabled ONLY if feature flag exists with enabled=true
  // Missing flags default to disabled
  const enabledKeys = new Set<string>()

  // Track all enabled flags
  featureToggles.forEach((flag) => {
    if (flag.enabled) {
      enabledKeys.add(flag.key)
    }
  })

  // Filter items recursively
  const filterItems = (menuItems: ItemType[]): ItemType[] => {
    return menuItems
      .map((item) => {
        if (!item) return null

        // If item has children, filter them first
        if ("children" in item && item.children && item.children.length > 0) {
          const filteredChildren = filterItems(
            item.children as ItemType[]
          ).filter((child) => {
            if (!child) return false
            // Keep child only if it has an enabled feature flag
            const childKey = String(child.key)
            return enabledKeys.has(childKey)
          })

          // If parent has no enabled children, filter out the parent
          if (filteredChildren.length === 0) {
            return null
          }

          // Return parent with filtered children
          return {
            ...item,
            children: filteredChildren,
          }
        }

        // Leaf item - keep only if it has an enabled feature flag
        const itemKey = String(item.key)
        if (!enabledKeys.has(itemKey)) {
          return null
        }

        return item
      })
      .filter((item): item is ItemType => item !== null)
  }

  return filterItems(items)
}

/**
 * Menu key to permission mapping
 * Maps navigation menu item keys to their required Resource and Action
 * Items without an entry here are always visible (e.g., dashboard)
 */
export const MENU_KEY_PERMISSIONS: Record<
  string,
  { resource: Resource; action: Action }
> = {
  // Members
  "members-directory": { resource: Resource.MEMBERSHIP, action: Action.READ },
  "members-groups-committees": {
    resource: Resource.GROUP,
    action: Action.READ,
  },
  "members-membership-tiers": {
    resource: Resource.MEMBERSHIP_TIER,
    action: Action.READ,
  },
  "members-invites": { resource: Resource.INVITATION, action: Action.READ },
  "members-households": {
    resource: Resource.MEMBERSHIP,
    action: Action.READ,
  },

  // Events
  "events-all-events": { resource: Resource.EVENT, action: Action.READ },
  "events-volunteering": { resource: Resource.EVENT, action: Action.READ },
  "events-calendar": { resource: Resource.EVENT, action: Action.READ },
  "events-attendance": { resource: Resource.EVENT, action: Action.READ },

  // Communication
  "communication-announcements": {
    resource: Resource.ANNOUNCEMENT,
    action: Action.READ,
  },
  "communication-newsletters": {
    resource: Resource.COMMUNICATION_TEMPLATES,
    action: Action.READ,
  },
  "communication-surveys": {
    resource: Resource.COMMUNICATION_TEMPLATES,
    action: Action.READ,
  },
  "communication-messages": {
    resource: Resource.COMMUNICATION_TEMPLATES,
    action: Action.READ,
  },
  "communication-email-campaigns": {
    resource: Resource.COMMUNICATION_TEMPLATES,
    action: Action.READ,
  },
  "communication-templates": {
    resource: Resource.COMMUNICATION_TEMPLATES,
    action: Action.READ,
  },

  // Finance
  "finance-overview": { resource: Resource.PAYMENT, action: Action.READ },
  "finance-invoices": { resource: Resource.INVOICE, action: Action.READ },
  "finance-orders": { resource: Resource.ORDER, action: Action.READ },
  "finance-dues": { resource: Resource.INVOICE, action: Action.READ },
  "finance-donations": { resource: Resource.DONATION, action: Action.READ },
  "finance-fundraising": { resource: Resource.PAYMENT, action: Action.READ },
  "finance-transactions": { resource: Resource.PAYMENT, action: Action.READ },
  "finance-reports": { resource: Resource.PAYMENT, action: Action.READ },

  // Governance
  "governance-elections": {
    resource: Resource.GOVERNANCE,
    action: Action.READ,
  },
  "governance-voting": { resource: Resource.GOVERNANCE, action: Action.READ },
  "governance-bylaws": { resource: Resource.GOVERNANCE, action: Action.READ },

  // Content
  "content-documents": { resource: Resource.CONTENT, action: Action.READ },
  "content-media": { resource: Resource.CONTENT, action: Action.READ },
  "content-resources": { resource: Resource.CONTENT, action: Action.READ },

  // Settings
  "settings-general": {
    resource: Resource.ORGANIZATION,
    action: Action.MANAGE,
  },
  "settings-organization-profile": {
    resource: Resource.ORGANIZATION,
    action: Action.READ,
  },
  "settings-roles-permissions": {
    resource: Resource.ROLE,
    action: Action.READ,
  },
  "settings-integrations": {
    resource: Resource.ORGANIZATION,
    action: Action.MANAGE,
  },
  "settings-billing": { resource: Resource.PAYMENT, action: Action.MANAGE },
}

/**
 * Filter navigation items based on user permissions
 * Removes children the user lacks permission to access
 * Removes parent items that have no permitted children remaining
 *
 * @param items - Navigation items to filter
 * @param hasPermission - Permission check function from useOrganizationPermissions
 * @returns Filtered navigation items
 */
export const filterNavigationByPermissions = (
  items: ItemType[],
  hasPermission: (resource: Resource, action: Action) => boolean
): ItemType[] => {
  return items
    .map((item) => {
      if (!item) return null

      if ("children" in item && item.children && item.children.length > 0) {
        const filteredChildren = (item.children as ItemType[]).filter(
          (child) => {
            if (!child) return false
            const childKey = String(child.key)
            const perm = MENU_KEY_PERMISSIONS[childKey]
            if (!perm) return true
            return hasPermission(perm.resource, perm.action)
          }
        )

        if (filteredChildren.length === 0) return null

        return { ...item, children: filteredChildren }
      }

      const itemKey = String(item.key)
      const perm = MENU_KEY_PERMISSIONS[itemKey]
      if (perm && !hasPermission(perm.resource, perm.action)) return null

      return item
    })
    .filter((item): item is ItemType => item !== null)
}
